import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl } from "../../../contexts/ContextProvider";
import { ConfirmDeleteDialog, InfoDialog, Loader, ScrollToTop } from "../../../components";
import { ShowTableDaftarUser } from "../../../components/ShowTable";
import { getSelectableMenuAccess } from "../../../constants/userMenuAccess";

const DaftarUser = () => {
  const { user, dispatch } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedId = location.pathname.split("/")[2];

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const selectedUser = users.find((item) => item._id === selectedId);
  const selectedUserAccess = getSelectableMenuAccess(selectedUser?.tipeUser);
  const selectedTipeUser =
    selectedUser?.tipeUser === "SPV/KOMITE" ||
    selectedUser?.tipeUser === "KOMITE/SPV"
      ? "SPV"
      : selectedUser?.tipeUser === "EKS"
      ? "KOLEKTOR"
      : selectedUser?.tipeUser;

  useEffect(() => {
    getUsers();
  }, []);

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      dispatch({ type: "LOGOUT" });
      navigate("/");
    }
  };

  const getUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${tempUrl}/users/`, {
        id: user._id,
        token: user.token,
      });
      setUsers(response.data);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil data user.");
    }
    setLoading(false);
  };

  const deleteUser = async (id) => {
    if (id === user._id) return;

    setShowDeleteConfirm(false);
    setLoading(true);
    try {
      await axios.post(`${tempUrl}/users/deleteUser/${id}`, {
        id: user._id,
        token: user.token,
      });
      navigate("/daftarUser");
      await getUsers();
    } catch (err) {
      handleAuthError(err);
      setDeleteError(err.response?.data?.message || "Gagal menghapus user.");
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <ScrollToTop />
      <div className="page-header">
        <div>
          <div className="page-kicker">Utility</div>
          <h1 className="page-title">Daftar User</h1>
        </div>
        <Button className="btn-icon" size="sm" onClick={() => navigate("/daftarUser/tambahUser")}>
          <MdAdd /> Tambah
        </Button>
      </div>
      {error && <Alert variant="warning">{error}</Alert>}

      {selectedUser && (
        <section className="section-panel">
          <div className="toolbar mt-0">
            <div className="detail-title-actions">
              <Button className="btn-back" size="sm" variant="outline-secondary" onClick={() => navigate("/daftarUser")}>
                {"< Kembali"}
              </Button>
              <div>
                <h2 className="h6 fw-bold mb-1">Detail User</h2>
                <div className="text-muted">{selectedUser.username}</div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                className="btn-icon"
                size="sm"
                variant="outline-primary"
                onClick={() => navigate(`/daftarUser/${selectedUser._id}/edit`)}
              >
                <MdEdit /> Ubah
              </Button>
              <Button
                className="btn-icon"
                size="sm"
                variant="outline-danger"
                disabled={selectedUser._id === user._id}
                onClick={() => setShowDeleteConfirm(true)}
              >
                <MdDelete /> Hapus
              </Button>
            </div>
          </div>
          <hr />
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <div className="detail-label">Username</div>
                <div className="detail-value">{selectedUser.username || "-"}</div>
              </div>
              <div className="mb-3">
                <div className="detail-label">Tipe User</div>
                <div className="detail-value">{selectedTipeUser || "-"}</div>
              </div>
              <div className="mb-3">
                <div className="detail-label">Cabang</div>
                <div className="detail-value">
                  {selectedUser.cabang?.namaCabang
                    ? `${selectedUser.cabang._id} - ${selectedUser.cabang.namaCabang}`
                    : selectedUser.cabang || "-"}
                </div>
              </div>
              <div className="mb-3">
                <div className="detail-label">Surveyor</div>
                <div className="detail-value">
                  {selectedUser.kodeSurveyor?.namaSurveyor
                    ? `${selectedUser.kodeSurveyor.kodeSurveyor} - ${selectedUser.kodeSurveyor.namaSurveyor}`
                    : "-"}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <div className="detail-label">Kolektor</div>
                <div className="detail-value">
                  {selectedUser.kodeKolektor?.namaKolektor
                    ? `${selectedUser.kodeKolektor.kodeKolektor} - ${selectedUser.kodeKolektor.namaKolektor}`
                    : "-"}
                </div>
              </div>
              <div className="mb-3">
                <div className="detail-label">Kode Kwitansi</div>
                <div className="detail-value">{selectedUser.kodeKwitansi || "-"}</div>
              </div>
              <div className="mb-3">
                <div className="detail-label">No Terakhir</div>
                <div className="detail-value">{selectedUser.noTerakhir || "-"}</div>
              </div>
              <div className="mb-3">
                <div className="detail-label">COA Kasir</div>
                <div className="detail-value">{selectedUser.coaKasir || "-"}</div>
              </div>
            </Col>
          </Row>
          <h2 className="h6 fw-bold mt-4 mb-2">Hak Akses</h2>
          <Row>
            <Col md={6}>
              {selectedUserAccess.includes("surveyor") && <Form.Check type="checkbox" label="Surveyor" checked={Boolean(selectedUser.akses?.surveyor)} disabled readOnly />}
              {selectedUserAccess.includes("kolektor") && <Form.Check type="checkbox" label="Kolektor" checked={Boolean(selectedUser.akses?.kolektor)} disabled readOnly />}
              {selectedUserAccess.includes("permohonan") && <Form.Check type="checkbox" label="Entry Permohonan" checked={Boolean(selectedUser.akses?.permohonan)} disabled readOnly />}
              {selectedUserAccess.includes("namaPemohon") && <Form.Check type="checkbox" label="Daftar Permohonan" checked={Boolean(selectedUser.akses?.namaPemohon)} disabled readOnly />}
              {selectedUserAccess.includes("admin") && <Form.Check type="checkbox" label="Data Permohonan" checked={Boolean(selectedUser.akses?.admin)} disabled readOnly />}
              {selectedUserAccess.includes("dataVerifikasi") && <Form.Check type="checkbox" label="Data Verifikasi" checked={Boolean(selectedUser.akses?.dataVerifikasi || selectedUser.akses?.verifikator)} disabled readOnly />}
              {selectedUserAccess.includes("validasiDataVerifikator") && <Form.Check type="checkbox" label="Validasi Data Verifikator" checked={Boolean(selectedUser.akses?.validasiDataVerifikator || selectedUser.akses?.adminVerifikator)} disabled readOnly />}
              {selectedUserAccess.includes("daftarKunjungan") && <Form.Check type="checkbox" label="Daftar Kunjungan" checked={Boolean(selectedUser.akses?.daftarKunjungan || selectedUser.akses?.surveyKolektor)} disabled readOnly />}
              {selectedUserAccess.includes("profilUser") && <Form.Check type="checkbox" label="Profil User" checked={Boolean(selectedUser.akses?.profilUser)} disabled readOnly />}
            </Col>
            <Col md={6}>
              {selectedUserAccess.includes("daftarUser") && <Form.Check type="checkbox" label="Daftar User" checked={Boolean(selectedUser.akses?.daftarUser)} disabled readOnly />}
              {selectedUserAccess.includes("setting") && <Form.Check type="checkbox" label="Setting" checked={Boolean(selectedUser.akses?.setting)} disabled readOnly />}
            </Col>
          </Row>
        </section>
      )}

      <ShowTableDaftarUser
        users={users}
        selectedId={selectedId}
        onSelect={(id) => navigate(`/daftarUser/${id}`)}
      />
      <ConfirmDeleteDialog
        show={showDeleteConfirm}
        message={`Yakin ingin menghapus user ${selectedUser?.username}?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteUser(selectedUser._id)}
      />
      <InfoDialog
        show={Boolean(deleteError)}
        title="Data Tidak Bisa Dihapus"
        message={deleteError}
        onClose={() => setDeleteError("")}
      />
    </div>
  );
};

export default DaftarUser;

